import React, { useEffect, useState } from 'react';
import * as mobxReact from 'mobx-react';
import classNames from 'classnames';
import { injectIntl, IntlShape } from 'react-intl';
import ReportsViewModel from '../reports.viewmodel';
import './generate-event-report.scss';
import { ReportConfigurationViewModel } from '../report-configuration/report-configuration.viewmodel';
import SavedConfigurations from '../report-configuration/_saved-configurations/_saved-configurations';
import {
    Category,
    LocalConfigurationStore,
    ReportType,
    GenerateConfigurationTab,
    ISelectableFilter,
    FilterField
} from '../../../../../api/reports.dao';
import { CommonPopupBodyFooterLayout } from '../../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../../global/sub-window/common/common-close-sub-window-buttons';
import EventReportColumnsTab from './_event-report-columns-tab/event-report-columns-tab';
import EventReportFiltersTab from './_event-report-filters-tab/event-report-filters-tab';
import { loadingText } from '../../../../../constants/common-labels.constants';

interface IEventGenerationModalProps {
    intl: IntlShape;
    vm: ReportsViewModel;
    reportType: ReportType;
    withoutConfiguration?: boolean;
    excludedCategories?: Category[];
    shouldAutoDownload?: boolean;
    excludedTabs?: GenerateConfigurationTab[];
    availableFilterTypes: ISelectableFilter[];
    withLayout?: boolean;
}

interface IBodyProps {
    intl: IntlShape;
    vm: ReportsViewModel;
    configurationVm: ReportConfigurationViewModel;
    withoutConfiguration: boolean;
    excludedTabs: GenerateConfigurationTab[];
}

const Body = injectIntl(mobxReact.observer((props: IBodyProps) => {
    const {
        vm,
        configurationVm,
        intl,
        withoutConfiguration,
        excludedTabs,
    } = props;

    const tabLabels = {
        [GenerateConfigurationTab.FILTERS]: intl.formatMessage({
            id: 'oe.reports.filters',
            defaultMessage: 'Filters',
            description: 'Tab label'
        }),
        [GenerateConfigurationTab.COLUMNS]: intl.formatMessage({
            id: 'oe.reports.columns',
            defaultMessage: 'Columns',
            description: 'Tab label'
        })
    };

    let currentTabs = Object.values(GenerateConfigurationTab);

    if (excludedTabs.length) {
        excludedTabs.filter(el => {
            const index = currentTabs.indexOf(el);
            if (index > -1) {
                currentTabs.splice(index, 1);
            }
        });
    }

    const defaultTab = currentTabs[0];
    const [selectedTab, setSelectedTab] = useState<GenerateConfigurationTab>(defaultTab);

    return (
        <div className='generate-event-report__body'>
            <div className='generate-event-report__header'>
                <div className='generate-event-report__tabs'>
                    {currentTabs.map(tab =>
                        <div key={tab} className={classNames('generate-event-report__tab', {
                            'generate-event-report__tab--selected': tab === selectedTab
                        })}
                            onClick={() => setSelectedTab(tab)}>
                            {tabLabels[tab]}
                        </div>
                    )}
                </div>
                {!withoutConfiguration && configurationVm && <SavedConfigurations
                    configurationVm={configurationVm}
                />}
            </div>
            { selectedTab === GenerateConfigurationTab.COLUMNS && <EventReportColumnsTab
                configurationVm={configurationVm}
            /> }
            { selectedTab === GenerateConfigurationTab.FILTERS && <EventReportFiltersTab
                configurationVm={configurationVm}
                vm={vm}
            /> }
        </div>
    );
}));

const GenerateEventReport = injectIntl(mobxReact.observer((props: IEventGenerationModalProps) => {
    const {
        intl,
        vm,
        reportType,
        withoutConfiguration = false,
        excludedCategories = [],
        excludedTabs = [],
        shouldAutoDownload = false,
        availableFilterTypes,
        withLayout
    } = props;
    const [configurationVm, setConfigurationVm] = useState<ReportConfigurationViewModel>();

    const reportTypeConfig = {
        [ReportType.REGISTRANTS_BY_SESSION_REPORT] : {
            disabled: vm.isGeneratingRegistrantsBySessionReport,
            storageName: LocalConfigurationStore.REGISTRANT_BY_SESSION,
            generateEventFn: () => vm.generateReport(ReportType.REGISTRANTS_BY_SESSION_REPORT, configurationVm, shouldAutoDownload)
        },
        [ReportType.EVENT_INTERACTIONS_REPORT] : {
            disabled: vm.isGeneratingEventInteractionsReport,
            storageName: LocalConfigurationStore.EVENT_INTERACTION,
            generateEventFn: () => vm.generateReport(ReportType.EVENT_INTERACTIONS_REPORT, configurationVm)
        },
        [ReportType.ATTENDEE_INTERACTIONS_REPORT] : {
            disabled: vm.isGeneratingAttendeesInteractionsReport
            || configurationVm?.appliedFilters.length === 0
            || !configurationVm?.appliedFilters.find(item => item.field === FilterField.MEETING_ID)
            || configurationVm?.appliedFilters.some(item => (item.field === FilterField.MEETING_ID && (item.values.length > 20 || !item.values.length))),
            storageName: LocalConfigurationStore.ATTENDEE_INTERACTION,
            generateEventFn: () => vm.generateReport(ReportType.ATTENDEE_INTERACTIONS_REPORT, configurationVm)
        },
        [ReportType.EVENT_REGISTRATION_REPORT] : {
            disabled: vm.isGeneratingEventRegistrationReport,
            storageName: LocalConfigurationStore.EVENT_REGISTRATION,
            generateEventFn: () => vm.generateReport(ReportType.EVENT_REGISTRATION_REPORT, configurationVm)
        }
    };

    const initializeConfigurationVm = async () => {
        const _configurationVm = new ReportConfigurationViewModel(
            vm.accountId,
            vm.event.eventId,
            reportType,
            excludedCategories,
            availableFilterTypes,
            vm.clearDates,
            null,
            reportTypeConfig[reportType].storageName,
            withLayout
        );
        await _configurationVm.initialize();
        setConfigurationVm(_configurationVm);
    };

    useEffect(() => {
        initializeConfigurationVm();
    }, []);

    if (!configurationVm) {
        return <CommonPopupBodyFooterLayout
            body={
                <div className='generate-event-report__loading'>
                    { loadingText() }
                </div>
            }
        />;
    }

    return (
        <CommonPopupBodyFooterLayout
            className={ classNames('generate-event-report', {
                'generate-event-report--registrants-by-session': reportType === ReportType.REGISTRANTS_BY_SESSION_REPORT
            })}
            body={
                <Body
                    vm={vm}
                    configurationVm={configurationVm}
                    withoutConfiguration={withoutConfiguration}
                    excludedTabs={excludedTabs}
                />
            }
            footer={
                <div className='buttons-block buttons-block--from-right'>
                    <DefaultCloseSubWindowButton
                        text={intl.formatMessage({
                            id: 'oe.reports.generateNow',
                            defaultMessage: 'Generate Now',
                            description: 'Button label'
                        })}
                        modifier={['fat', 'confirm-safe']}
                        onClick={reportTypeConfig[reportType].generateEventFn}
                        disabled={reportTypeConfig[reportType].disabled || !configurationVm.currentConfiguration.some(el => el.enabled)}
                    />
                    <DefaultCloseSubWindowButton />
                </div>
            }
        />
    );
}));

export { GenerateEventReport };
